import React from 'react';
import { useSelector } from 'react-redux';
import { useMedia } from 'react-use';
import styled from 'styled-components';
import type { RootState } from '../../../../state/store';
import { getGreeting } from '../../helpers/utils';
import { useTheme } from '../../theme';
import MessageBox from '../MessageBox';
import PromptSuggestions from '../PromptSuggestions';

const MainView: React.FC = () => {
  const theme = useTheme();
  const companyDetails = useSelector((state: RootState) => state.company?.company_details);
  const userName = companyDetails?.user_name || 'Staff';
  const greeting = getGreeting(userName);
  const isMobile = useMedia('(max-width: 768px)');

  return (
    <Wrapper $theme={theme}>
      {isMobile ? (
        <>
          <HeroGreeting $theme={theme} $isMobile={isMobile}>
            {greeting}
          </HeroGreeting>
          <MobileContent>
            <MobileSuggestionsContainer>
              <PromptSuggestions />
            </MobileSuggestionsContainer>

            <MessageBoxWrapper>
              <MessageBox
                placeholder="Ask questions about your business data to get actionable insights..."
                isSticky={true}
                isMainView={true}
              />
            </MessageBoxWrapper>
          </MobileContent>
        </>
      ) : (
        <DesktopContent>
          <HeroGreeting $theme={theme} $isMobile={isMobile}>
            {greeting}
          </HeroGreeting>

          <MessageBoxWrapper>
            <MessageBox
              placeholder="Ask questions about your business data to get actionable insights..."
              isSticky={true}
              isMainView={true}
            />
          </MessageBoxWrapper>

          <DesktopSuggestionsContainer>
            <PromptSuggestions />
          </DesktopSuggestionsContainer>
        </DesktopContent>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $theme: any }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${(props) => props.$theme.colors.mainBg};
  color: ${(props) => props.$theme.colors.mainText};
  padding: 40px 24px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 0 0 140px 0;
  }
`;

const DesktopContent = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 16px;
  margin: 0 auto;
`;

const MobileContent = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  gap: 12px;
  margin: 0 auto;
  padding: 0 16px 16px;
  min-height: 0;
  overflow: hidden;
`;

const HeroGreeting = styled.h2<{ $theme: any; $isMobile: boolean }>`
  font-size: ${(props) => (props.$isMobile ? '26px' : '40px')};
  font-weight: 600;
  text-align: center;
  color: ${(props) => props.$theme.colors.mainText};
  margin: ${(props) => (props.$isMobile ? '0' : '0 0 24px 0')};
  line-height: 1.3;
  max-width: 800px;
  align-self: center;
  flex-shrink: 0;

  ${(props) =>
    props.$isMobile &&
    `
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: ${props.$theme.colors.mainBg};
    padding: 16px;
    margin: 0 0 8px;
    border-bottom: 1px solid ${props.$theme.colors.messageBoxBorder}20;
  `}

  @media (max-width: 1024px) {
    font-size: ${(props) => (props.$isMobile ? '26px' : '36px')};
    margin: ${(props) => (props.$isMobile ? '0' : '0 0 20px 0')};
  }

  @media (max-width: 480px) {
    font-size: 22px;
    ${(props) => props.$isMobile && `padding: 14px;`}
  }

  @media (max-width: 375px) {
    font-size: 20px;
    line-height: 1.2;
    ${(props) => props.$isMobile && `padding: 12px;`}
  }
`;

const MessageBoxWrapper = styled.div`
  width: 100%;
  max-width: 820px;
  flex-shrink: 0;
`;

const MobileSuggestionsContainer = styled.div`
  width: 100%;
  max-width: 820px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
`;

const DesktopSuggestionsContainer = styled.div`
  width: 100%;
  max-width: 820px;
`;

export default MainView;
